import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useOutletContext, useParams } from "react-router-dom";
import { useAuth } from "../../services/AuthContext";
import { markLearnPageSeen } from "../../utils/learnSeen";
import {
  IoArrowBackOutline,
  IoBookOutline,
  IoChevronDownOutline,
  IoChevronForwardOutline,
  IoCloseCircleOutline,
  IoHelpCircleOutline,
  IoLibraryOutline,
  IoLogoYoutube,
  IoOpenOutline,
  IoPlayCircleOutline,
  IoRocketOutline,
  IoSearchOutline,
  IoSchoolOutline,
  IoVideocamOutline,
} from "react-icons/io5";
import { getLanguageCopy, normalizeLanguage, translatePair } from "../../services/language";
import LearnSeoPage from "../../components/learn/LearnSeoPage";
import SeoMeta from "../../components/seo/SeoMeta";
import {
  getLearnSeoPage,
  learnCompleteNavigationGroups,
} from "../../content/learnPages";
import { getLearnGuideSearchResults, getLearnGuideSearchTokens } from "../../utils/learnGuideSearch";
import {
  LEARN_HERO_IMAGE,
  LEARN_OG_IMAGE,
  PUBLIC_LEARN_BASE,
  isPublicLearnPathname,
  normalizeLearnContentPath,
  publicLearnRoute,
  resolveLearnPathForMode,
} from "../../utils/learnRoutes";

const LIBRARY_JSON_URL = "/learn/library.json";
const RULES_BASE_PATH = "/rules";
const LEARN_LAST_UPDATED = "2026-07-22";
const XIMA_PLAY_URL = "https://juegoxima.com/";

const asArray = (value) => (Array.isArray(value) ? value : []);

const XIMA_RULES_OBJECTIVE = {
  es: "Llevar el bloque especial a la Cima del tablero.",
  en: "Bring the special block to the top of the board.",
  de: "Bringen Sie den Spezialblock auf die Spitze des Spielbretts.",
  ru: "Доведите специальный блок до вершины игрового поля.",
};

const normalizeRulesPayload = (payload) =>
  asArray(payload)
    .map((entry) => ({
      id: Number(entry?.id),
      text: String(entry?.text || "").trim(),
    }))
    .filter((entry) => Number.isFinite(entry.id) && entry.id > 0 && entry.text)
    .sort((left, right) => left.id - right.id);

const mergeRulesWithSpanishFallback = (localizedRules, spanishRules) => {
  const localizedById = new Map(
    normalizeRulesPayload(localizedRules).map((entry) => [entry.id, entry.text])
  );
  return normalizeRulesPayload(spanishRules).map((entry) => ({
    id: entry.id,
    text: localizedById.get(entry.id) || entry.text,
  }));
};

const loadRulesForLanguage = async (language) => {
  const loadRulesFile = async (languageCode) => {
    const response = await fetch(`${RULES_BASE_PATH}/${languageCode}/xima-rules.json`, {
      method: "GET",
      cache: "no-store",
    });
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    return response.json();
  };

  const spanishRules = await loadRulesFile("es");
  if (language === "es") {
    return normalizeRulesPayload(spanishRules);
  }

  try {
    const localizedRules = await loadRulesFile(language);
    return mergeRulesWithSpanishFallback(localizedRules, spanishRules);
  } catch (error) {
    return normalizeRulesPayload(spanishRules);
  }
};

const HOW_TO_PLAY_VIDEOS = [
  {
    id: "how-to-es",
    title: {
      es: "Como jugar XIMA",
      en: "How to Play XIMA (Spanish)",
      ru: "Как играть в XIMA",
      de: "So spielt man XIMA",
    },
    description: {
      es: "Guia rapida en espanol para aprender el flujo de juego.",
      en: "Quick tutorial in Spanish to learn the gameplay flow.",
      ru: "Короткий урок на испанском, чтобы понять ход игры.",
      de: "Kurzes Tutorial auf Spanisch, um den Spielablauf zu lernen.",
    },
    watchUrl: "https://www.youtube.com/watch?v=NlSq0nlnHYM",
    embedUrl: "https://www.youtube.com/embed/NlSq0nlnHYM",
  },
  {
    id: "how-to-en",
    title: {
      es: "Como jugar XIMA en menos de un minuto (ingles)",
      en: "How to Play XIMA in Less Than a Minute",
      ru: "Как играть в XIMA меньше чем за минуту",
      de: "XIMA in weniger als einer Minute",
    },
    description: {
      es: "Resumen en ingles con lo esencial para empezar.",
      en: "English summary with the essentials to start playing.",
      ru: "Краткое объяснение на английском с основами для старта.",
      de: "Kurze englische Zusammenfassung mit allem Wichtigen zum Start.",
    },
    watchUrl: "https://www.youtube.com/watch?v=bBmdNDj3PpA",
    embedUrl: "https://www.youtube.com/embed/bBmdNDj3PpA",
  },
];

const OFFICIAL_YOUTUBE_LINKS = {
  es: "https://www.youtube.com/@YosdelMuino",
  en: "https://www.youtube.com/@YVMAxima",
  ru: "https://www.youtube.com/@YVMAxima",
  de: "https://www.youtube.com/@YVMAxima",
};

const LEARN_COMPLETE_GUIDE_GROUP_LABELS = learnCompleteNavigationGroups.map((group) => group.label);
const LEARN_COMPLETE_GUIDE_COUNT = learnCompleteNavigationGroups.reduce(
  (total, group) => total + group.items.length,
  0
);
const LEARN_DEFAULT_OPEN_GUIDE_GROUP = LEARN_COMPLETE_GUIDE_GROUP_LABELS[0] || "";

const sectionAnchorId = (sectionId, index) =>
  `learn-section-${String(sectionId || "section").replace(/[^a-zA-Z0-9_-]/g, "-")}-${index}`;

const LEARN_ART_CUBES = ["blue", "cyan", "white", "red", "green", "violet", "gold", "blue"];

const LearnCubeArt = ({ variant = "default" }) => (
  <div className={`xima-learn-art xima-learn-art-${variant}`} aria-hidden="true">
    <div className="xima-learn-board-grid" />
    <div className="xima-learn-cube-stack">
      {LEARN_ART_CUBES.map((tone, index) => (
        <span key={`${tone}-${index}`} className={`xima-learn-cube xima-learn-cube-${tone}`} />
      ))}
    </div>
  </div>
);

const getBookCardCopy = (book, language) => {
  const slug = String(book?.slug || "").toLowerCase();
  if (slug.includes("cientifica")) {
    return {
      title: book?.title || "Xima Cientifica",
      description: translatePair(
        language,
        "Domina la ciencia, las alturas y el control del tablero.",
        "Master science, heights, and board control."
      ),
      theme: "violet",
      image: "/learn/assets/xima-cientifica/image7.png",
    };
  }

  return {
    title: book?.title || "Como llegar a la Cima",
    description: translatePair(
      language,
      "Estrategias, alturas, diagonales y caminos ganadores.",
      "Strategies, heights, diagonals, and winning paths."
    ),
    theme: "blue",
    image: "/learn/assets/como-llegar-a-la-xima/image3.png",
  };
};

const Learn = ({ publicMode = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const wildcardPath = params["*"];
  const legacyBookSlug = params.bookSlug;
  const rawLearnPath = useMemo(
    () =>
      String(wildcardPath ?? legacyBookSlug ?? "")
        .trim()
        .replace(/^\/+|\/+$/g, "")
        .toLowerCase(),
    [legacyBookSlug, wildcardPath]
  );
  const learnPath = useMemo(() => normalizeLearnContentPath(rawLearnPath), [rawLearnPath]);
  const { language: outletLanguage = "es" } = useOutletContext() || {};
  const language = normalizeLanguage(outletLanguage);
  const isPublicRoute = publicMode || isPublicLearnPathname(location.pathname);
  const fromLobbySource = useMemo(
    () => String(new URLSearchParams(location.search).get("fromLobbySource") || "").toLowerCase(),
    [location.search]
  );
  const learnReturnRoute = fromLobbySource === "learn" ? "/game/lobby" : "/game";
  const learnHomeRoute = isPublicRoute ? PUBLIC_LEARN_BASE : "/game/learn";

  const hrefFor = (path) => resolveLearnPathForMode(path, isPublicRoute);

  const goTo = (path, options) => {
    const targetPath = hrefFor(path);
    if (!targetPath) return;
    if (targetPath.startsWith("/#")) {
      if (options?.replace) window.location.replace(targetPath);
      else window.location.assign(targetPath);
      return;
    }
    navigate(targetPath, options);
  };

  const [library, setLibrary] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [activeSectionIndex, setActiveSectionIndex] = useState(0);
  const [rules, setRules] = useState([]);
  const [openGuideGroups, setOpenGuideGroups] = useState(
    () => new Set([LEARN_DEFAULT_OPEN_GUIDE_GROUP].filter(Boolean))
  );
  const [guideSearchTerm, setGuideSearchTerm] = useState("");

  const t = (es, en) => translatePair(language, es, en);
  const guideSearchGroupLabel = useMemo(
    () => translatePair(language, "Resultados principales", "Top results"),
    [language]
  );
  const objectiveText = getLanguageCopy(language, XIMA_RULES_OBJECTIVE, XIMA_RULES_OBJECTIVE.es);
  const guideSearchTokens = useMemo(
    () => getLearnGuideSearchTokens(guideSearchTerm),
    [guideSearchTerm]
  );
  const isGuideSearchActive = guideSearchTokens.length > 0;
  const visibleGuideGroups = useMemo(() => {
    if (!isGuideSearchActive) return learnCompleteNavigationGroups;

    const searchResults = getLearnGuideSearchResults(learnCompleteNavigationGroups, guideSearchTerm);
    return searchResults.length > 0
      ? [
          {
            label: guideSearchGroupLabel,
            items: searchResults,
          },
        ]
      : [];
  }, [guideSearchGroupLabel, guideSearchTerm, isGuideSearchActive]);
  const visibleGuideCount = visibleGuideGroups.reduce((total, group) => total + group.items.length, 0);
  const areAllGuideGroupsOpen = openGuideGroups.size === LEARN_COMPLETE_GUIDE_GROUP_LABELS.length;

  const toggleGuideGroup = (groupLabel) => {
    if (isGuideSearchActive) return;
    setOpenGuideGroups((currentGroups) => {
      const nextGroups = new Set(currentGroups);
      if (nextGroups.has(groupLabel)) {
        nextGroups.delete(groupLabel);
      } else {
        nextGroups.add(groupLabel);
      }
      return nextGroups;
    });
  };

  const openAllGuideGroups = () => {
    setOpenGuideGroups(new Set(LEARN_COMPLETE_GUIDE_GROUP_LABELS));
  };

  const closeAllGuideGroups = () => {
    setOpenGuideGroups(new Set());
  };

  useEffect(() => {
    let mounted = true;

    const loadLibrary = async () => {
      setIsLoading(true);
      setErrorMessage("");
      try {
        const response = await fetch(LIBRARY_JSON_URL, { cache: "no-store" });
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        const data = await response.json();
        if (!mounted) return;
        setLibrary(data);
      } catch (error) {
        if (!mounted) return;
        setErrorMessage(
          language === "es"
            ? "No se pudo cargar la biblioteca interna."
            : "Could not load the internal library."
        );
      } finally {
        if (mounted) setIsLoading(false);
      }
    };

    void loadLibrary();

    return () => {
      mounted = false;
    };
  }, [language]);

  useEffect(() => {
    let mounted = true;

    const loadRules = async () => {
      try {
        const nextRules = await loadRulesForLanguage(language);
        if (!mounted) return;
        setRules(nextRules);
      } catch (error) {
        if (!mounted) return;
        setRules([]);
      }
    };

    void loadRules();

    return () => {
      mounted = false;
    };
  }, [language]);

  const books = useMemo(() => asArray(library?.books), [library]);

  const selectedBook = useMemo(
    () => books.find((book) => String(book?.slug || "").toLowerCase() === learnPath) || null,
    [books, learnPath]
  );
  const selectedSeoPage = useMemo(() => getLearnSeoPage(learnPath), [learnPath]);
  // Cada página abierta deja de contar en la bolita de «Aprender» del menú.
  const { user: learnUser } = useAuth() || {};
  useEffect(() => {
    if (publicMode || !selectedSeoPage?.slug) return;
    markLearnPageSeen(learnUser?.loginIdentifier || learnUser?.email || "", selectedSeoPage.slug);
  }, [learnUser, publicMode, selectedSeoPage]);

  const sections = useMemo(() => asArray(selectedBook?.sections), [selectedBook]);
  const toc = useMemo(() => {
    const rawToc = asArray(selectedBook?.toc);
    if (rawToc.length === sections.length && rawToc.length > 0) return rawToc;
    return sections.map((section) => ({ title: section?.title || "", page: section?.page || null }));
  }, [selectedBook, sections]);
  const introduction = useMemo(() => asArray(selectedBook?.introduction), [selectedBook]);

  useEffect(() => {
    setActiveSectionIndex(0);
  }, [learnPath]);

  useEffect(() => {
    if (!isPublicRoute && !isLoading && learnPath && !selectedBook && !selectedSeoPage && books.length > 0) {
      navigate(learnHomeRoute, { replace: true });
    }
  }, [books.length, isLoading, isPublicRoute, learnHomeRoute, learnPath, navigate, selectedBook, selectedSeoPage]);

  const scrollToSection = (index) => {
    if (index < 0 || index >= sections.length) return;
    const section = sections[index];
    const targetId = sectionAnchorId(section?.id, index);
    const target = typeof document !== "undefined" ? document.getElementById(targetId) : null;
    if (!target) return;
    target.scrollIntoView({ behavior: "smooth", block: "start" });
    setActiveSectionIndex(index);
  };

  const renderContentBlock = (block, blockIndex) => {
    if (!block || typeof block !== "object") return null;

    if (block.type === "image" && block.src) {
      return (
        <figure key={`img-${blockIndex}`} className="overflow-hidden rounded-lg border border-[#2f4b78] bg-[#081327]">
          <img
            src={block.src}
            alt={t("Ilustracion del libro", "Book illustration")}
            className="h-auto w-full object-contain"
            loading="lazy"
          />
        </figure>
      );
    }

    if (block.type === "table") {
      const rows = asArray(block.rows);
      if (!rows.length) return null;
      return (
        <div key={`tbl-${blockIndex}`} className="overflow-auto rounded-lg border border-[#2f4b78] bg-[#081327]">
          <table className="min-w-full border-collapse text-left text-xs text-[#dce8ff] md:text-sm">
            <tbody>
              {rows.map((row, rowIndex) => (
                <tr key={`row-${rowIndex}`} className="border-b border-[#2f4b78] last:border-b-0">
                  {asArray(row).map((cell, cellIndex) => (
                    <td key={`cell-${rowIndex}-${cellIndex}`} className="px-3 py-2 align-top">
                      {cell || "-"}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    if (block.type === "paragraph" && block.text) {
      if (block.isList) {
        return (
          <div key={`li-${blockIndex}`} className="flex items-start gap-2 text-sm text-[#dce8ff] md:text-base">
            <span className="mt-[7px] inline-block h-1.5 w-1.5 rounded-full bg-[#63d5ff]" />
            <span>{block.text}</span>
          </div>
        );
      }

      return (
        <p key={`p-${blockIndex}`} className="text-sm leading-relaxed text-[#dce8ff] md:text-base">
          {block.text}
        </p>
      );
    }

    return null;
  };

  if (selectedSeoPage) {
    return <LearnSeoPage page={selectedSeoPage} publicMode={isPublicRoute} />;
  }

  if (isLoading && learnPath) {
    return (
      <div className="xima-learn-screen">
        <div className="xima-learn-shell">
          <div className="xima-learn-status-card">
          {t("Cargando biblioteca de Aprender...", "Loading Learn library...")}
          </div>
        </div>
      </div>
    );
  }

  if (errorMessage && learnPath) {
    return (
      <div className="xima-learn-screen">
        <div className="xima-learn-shell">
          <div className="xima-learn-status-card xima-learn-status-error">
          {errorMessage}
          </div>
        </div>
      </div>
    );
  }

  if (!isLoading && learnPath && !selectedBook && !selectedSeoPage && isPublicRoute) {
    return (
      <div className="xima-learn-screen">
        <SeoMeta
          title="Pagina no encontrada | Aprender XIMA"
          description="La guia solicitada no existe en Aprender XIMA. Vuelve al indice para encontrar reglas, modos, tutoriales y soporte."
          canonicalUrl="https://juegoxima.com/aprender"
          ogTitle="Pagina no encontrada | Aprender XIMA"
          ogDescription="La guia solicitada no existe en Aprender XIMA. Vuelve al indice de Aprender."
          ogImage={LEARN_OG_IMAGE}
          robots="noindex,follow"
          datePublished={LEARN_LAST_UPDATED}
          dateModified={LEARN_LAST_UPDATED}
          structuredData={{
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: "Pagina no encontrada | Aprender XIMA",
            description: "La guia solicitada no existe en Aprender XIMA.",
            url: "https://juegoxima.com/aprender",
            isPartOf: {
              "@type": "WebSite",
              name: "XIMA",
              url: "https://juegoxima.com",
            },
          }}
        />
        <div className="xima-learn-shell">
          <section className="xima-learn-hero xima-learn-seo-hero">
            <div className="xima-learn-hero-copy">
              <span className="xima-learn-title-icon">
                <IoBookOutline aria-hidden="true" />
              </span>
              <div>
                <span className="xima-learn-kicker">Aprender XIMA</span>
                <h1>Pagina no encontrada</h1>
                <p>
                  La guia solicitada no existe o todavia no esta disponible en la base publica de Aprender.
                </p>
              </div>
            </div>
            <div className="xima-learn-hero-actions">
              <a href={PUBLIC_LEARN_BASE} className="xima-learn-green-button">
                <IoBookOutline aria-hidden="true" />
                <span>Volver a Aprender</span>
              </a>
              <a href={XIMA_PLAY_URL} className="xima-learn-play-button" aria-label="Jugar XIMA">
                <IoRocketOutline aria-hidden="true" />
                <span>JUGAR</span>
              </a>
            </div>
          </section>
          <section className="xima-learn-panel xima-learn-seo-article">
            <h2>Que puedes hacer</h2>
            <div className="xima-learn-seo-sections">
              <div className="xima-learn-seo-section-block">
                <h3>Buscar una guia real</h3>
                <p>
                  Entra al indice de Aprender para consultar reglas, modos de juego, tutoriales, historial,
                  replay, glosario o soporte.
                </p>
              </div>
              <div className="xima-learn-seo-section-block">
                <h3>Comprobar la URL</h3>
                <p>
                  Si escribiste la direccion manualmente, revisa que no falten palabras, guiones o secciones.
                </p>
              </div>
            </div>
            <div className="xima-learn-seo-link-list">
              <a href={PUBLIC_LEARN_BASE}>
                <span>Indice de Aprender</span>
                <IoChevronForwardOutline aria-hidden="true" />
              </a>
              <a href={`${PUBLIC_LEARN_BASE}/como-jugar`}>
                <span>Como jugar XIMA</span>
                <IoChevronForwardOutline aria-hidden="true" />
              </a>
              <a href={`${PUBLIC_LEARN_BASE}/reglas`}>
                <span>Reglas de XIMA</span>
                <IoChevronForwardOutline aria-hidden="true" />
              </a>
            </div>
          </section>
        </div>
      </div>
    );
  }

  if (selectedBook) {
    const selectedBookCard = getBookCardCopy(selectedBook, language);

    return (
      <div className="xima-learn-screen">
        <SeoMeta
          title={`${selectedBook.title} | Biblioteca XIMA`}
          description={selectedBookCard.description}
          canonicalUrl={`https://juegoxima.com/aprender/biblioteca/${selectedBook.slug}`}
          ogTitle={`${selectedBook.title} | Biblioteca XIMA`}
          ogDescription={selectedBookCard.description}
          ogImage={LEARN_OG_IMAGE}
          robots="index,follow"
          datePublished={LEARN_LAST_UPDATED}
          dateModified={LEARN_LAST_UPDATED}
          structuredData={{
            "@context": "https://schema.org",
            "@type": "Article",
            headline: selectedBook.title,
            description: selectedBookCard.description,
            url: `https://juegoxima.com/aprender/biblioteca/${selectedBook.slug}`,
            image: LEARN_OG_IMAGE,
            datePublished: LEARN_LAST_UPDATED,
            dateModified: LEARN_LAST_UPDATED,
          }}
        />
        <div className="xima-learn-shell">
          <section className="xima-learn-hero xima-learn-book-hero">
            <div className="xima-learn-hero-copy">
              <span className="xima-learn-title-icon">
                <IoBookOutline aria-hidden="true" />
              </span>
              <div>
                <span className="xima-learn-kicker">{t("Biblioteca Interna", "Internal Library")}</span>
                <h1>{selectedBook.title}</h1>
                <p>{selectedBookCard.description}</p>
              </div>
            </div>
            <div className="xima-learn-hero-actions">
              <button type="button" onClick={() => goTo("/game/learn")} className="xima-learn-outline-button">
                <IoArrowBackOutline aria-hidden="true" />
                <span>{t("Volver a Aprender", "Back to Learn")}</span>
              </button>
              {isPublicRoute ? (
                <a href={XIMA_PLAY_URL} className="xima-learn-play-button" aria-label="Jugar XIMA">
                  <IoRocketOutline aria-hidden="true" />
                  <span>JUGAR</span>
                </a>
              ) : (
                <button type="button" onClick={() => goTo("/game")} className="xima-learn-green-button">
                  <IoRocketOutline aria-hidden="true" />
                  <span>{t("Inicio XIMA", "XIMA Home")}</span>
                </button>
              )}
            </div>
            <div className="xima-learn-hero-art">
              <LearnCubeArt variant={selectedBookCard.theme} />
            </div>
          </section>

          <section className="xima-learn-book-layout">
            <aside className="xima-learn-book-aside">
              <LearnCubeArt variant={selectedBookCard.theme} />
              <div className="xima-learn-aside-title">
                <IoLibraryOutline aria-hidden="true" />
                <span>{t("Indice", "Index")}</span>
              </div>
              <ul className="xima-learn-toc-list">
                {toc.map((item, index) => {
                  const label = item?.title || `${t("Seccion", "Section")} ${index + 1}`;
                  const isActive = index === activeSectionIndex;
                  return (
                    <li key={`${label}-${index}`}>
                      <button
                        type="button"
                        onClick={() => scrollToSection(index)}
                        className={`xima-learn-toc-button ${isActive ? "xima-learn-toc-active" : ""}`}
                      >
                        <span>{label}</span>
                        {item?.page ? (
                          <small>
                            {t("Pag.", "Page")} {item.page}
                          </small>
                        ) : null}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </aside>

            <div className="xima-learn-book-content">
              <article className="xima-learn-panel xima-learn-book-article">
                <h2>
                  {t("Introduccion", "Introduction")}
                </h2>
                <div className="xima-learn-book-blocks">
                  {introduction.length > 0 ? (
                    introduction.map((block, index) => renderContentBlock(block, index))
                  ) : (
                    <p>
                      {t(
                        "Este libro no tiene introduccion marcada. Puedes empezar por la primera seccion.",
                        "This book has no explicit introduction marker. You can start with the first section."
                      )}
                    </p>
                  )}
                </div>
              </article>

              {sections.map((section, sectionIndex) => (
                <article
                  key={`${section.id}-${sectionIndex}`}
                  id={sectionAnchorId(section.id, sectionIndex)}
                  className="xima-learn-panel xima-learn-book-article"
                >
                  <div className="xima-learn-book-article-head">
                    <h3>
                      {section.title}
                    </h3>
                    {section.page ? (
                      <span>
                        {t("Pag.", "Page")} {section.page}
                      </span>
                    ) : null}
                  </div>
                  <div className="xima-learn-book-blocks">
                    {asArray(section.blocks).map((block, blockIndex) => renderContentBlock(block, blockIndex))}
                  </div>
                </article>
              ))}

              {sections.length > 0 ? (
                <div className="xima-learn-book-nav">
                  <button
                    type="button"
                    onClick={() => scrollToSection(Math.max(0, activeSectionIndex - 1))}
                    className="xima-learn-outline-button"
                    disabled={activeSectionIndex <= 0}
                  >
                    <IoArrowBackOutline aria-hidden="true" />
                    {t("Seccion anterior", "Previous section")}
                  </button>
                  <button
                    type="button"
                    onClick={() => scrollToSection(Math.min(sections.length - 1, activeSectionIndex + 1))}
                    className="xima-learn-outline-button"
                    disabled={activeSectionIndex >= sections.length - 1}
                  >
                    {t("Siguiente seccion", "Next section")}
                    <IoChevronForwardOutline aria-hidden="true" />
                  </button>
                </div>
              ) : null}
            </div>
          </section>
        </div>
      </div>
    );
  }

  return (
    <div className="xima-learn-screen">
      <SeoMeta
        title="Aprender XIMA | Reglas, modos y guias oficiales"
        description="Aprende XIMA con reglas verificadas, modos de juego, tutoriales, biblioteca, historial, replay, glosario y soporte."
        canonicalUrl="https://juegoxima.com/aprender"
        ogTitle="Aprender XIMA"
        ogDescription="Reglas, modos, tutoriales y guias para jugar XIMA."
        ogImage={LEARN_OG_IMAGE}
        robots="index,follow"
        datePublished={LEARN_LAST_UPDATED}
        dateModified={LEARN_LAST_UPDATED}
        structuredData={{
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "Aprender XIMA",
          description:
            "Reglas, modos, tutoriales y guias para jugar XIMA.",
          url: "https://juegoxima.com/aprender",
          image: LEARN_OG_IMAGE,
          datePublished: LEARN_LAST_UPDATED,
          dateModified: LEARN_LAST_UPDATED,
        }}
      />
      <div className="xima-learn-shell">
        <section className="xima-learn-hero">
          <div className="xima-learn-hero-copy">
            <span className="xima-learn-title-icon">
              <IoSchoolOutline aria-hidden="true" />
            </span>
            <div>
              <h1>{t("Aprender XIMA", "Learn XIMA")}</h1>
              <p>
                {t(
                  "Aqui puedes encontrar guias, videos oficiales y reglas detalladas sobre como jugar XIMA y dominar la cima.",
                  "Find guides, official videos, and detailed rules about how to play XIMA and master the summit."
                )}
              </p>
            </div>
          </div>
          <div className="xima-learn-hero-actions">
            {isPublicRoute ? (
              <a href={XIMA_PLAY_URL} className="xima-learn-play-button" aria-label="Jugar XIMA">
                <IoPlayCircleOutline aria-hidden="true" />
                <span>JUGAR</span>
              </a>
            ) : (
              <button type="button" onClick={() => goTo(learnReturnRoute)} className="xima-learn-back-button">
                <IoPlayCircleOutline aria-hidden="true" />
                <span>{t("Volver al Lobby", "Back to Lobby")}</span>
              </button>
            )}
          </div>
          <div className="xima-learn-hero-art xima-learn-hero-image-wrap">
            <img src={LEARN_HERO_IMAGE} alt="" aria-hidden="true" />
          </div>
        </section>

        <section className="xima-learn-panel xima-learn-video-panel">
          <div className="xima-learn-section-title">
            <span className="xima-learn-section-icon xima-learn-section-icon-blue">
              <IoVideocamOutline aria-hidden="true" />
            </span>
            <div>
              <small>{t("Tutoriales en video", "Video Tutorials")}</small>
              <h2>{t("Como jugar XIMA", "How to Play XIMA")}</h2>
            </div>
          </div>

          <div className="xima-learn-video-grid">
            {HOW_TO_PLAY_VIDEOS.map((video, index) => (
              <article key={video.id} className="xima-learn-video-card">
                <h3>{getLanguageCopy(language, video.title)}</h3>
                <p>{getLanguageCopy(language, video.description)}</p>
                <div className="xima-learn-video-frame">
                  <iframe
                    title={video.id}
                    src={video.embedUrl}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                  <span className="xima-learn-video-duration">{index === 0 ? "05:32" : "08:47"}</span>
                </div>
                <a href={video.watchUrl} target="_blank" rel="noreferrer" className="xima-learn-youtube-link">
                  <span>{t("Ver en YouTube", "Watch on YouTube")}</span>
                  <IoOpenOutline aria-hidden="true" />
                </a>
              </article>
            ))}
          </div>
        </section>

        <section className="xima-learn-panel xima-learn-rules-panel">
          <div className="xima-learn-section-title">
            <span className="xima-learn-section-icon xima-learn-section-icon-violet">
              <IoLibraryOutline aria-hidden="true" />
            </span>
            <div>
              <small>{t("Guia oficial", "Official Guide")}</small>
              <h2>{t("Reglas de la Xima", "Rules of Xima")}</h2>
            </div>
          </div>
          <p className="xima-learn-rules-intro">
            {t(
              "Reglas para jugar XIMA y llegar a la cima con inteligencia, estrategia y ciencia. Lee atentamente.",
              "Rules to play XIMA and reach the summit with intelligence, strategy, and science. Read carefully."
            )}
          </p>
          <ol className="xima-learn-rule-list">
            <li>
              <span>{t("Objetivo del juego:", "Game objective:")}</span>
              <p>{objectiveText}</p>
            </li>
            {rules.map((rule) => (
              <li key={`${rule.id}-${rule.text.slice(0, 24)}`}>
                <span>{String(rule.id + 1).padStart(2, "0")}.</span>
                <p>{rule.text}</p>
              </li>
            ))}
          </ol>
        </section>

        <section className="xima-learn-panel xima-learn-seo-index-panel xima-learn-complete-index-panel">
          <div className="xima-learn-index-heading-row">
            <div className="xima-learn-section-title">
              <span className="xima-learn-section-icon xima-learn-section-icon-blue">
                <IoBookOutline aria-hidden="true" />
              </span>
              <div>
                <small>{t("Indice completo", "Complete Index")}</small>
                <h2>{t("Guias por tema", "Guides by Topic")}</h2>
              </div>
            </div>
            <div className="xima-learn-index-tools">
              <span className="xima-learn-index-count">
                {isGuideSearchActive ? visibleGuideCount : LEARN_COMPLETE_GUIDE_COUNT}{" "}
                {t("paginas", "pages")}
              </span>
              <div className="xima-learn-index-actions">
                <button
                  type="button"
                  className="xima-learn-index-action-button"
                  onClick={openAllGuideGroups}
                  disabled={isGuideSearchActive || areAllGuideGroupsOpen}
                >
                  {t("Abrir todo", "Open all")}
                </button>
                <button
                  type="button"
                  className="xima-learn-index-action-button"
                  onClick={closeAllGuideGroups}
                  disabled={isGuideSearchActive || openGuideGroups.size === 0}
                >
                  {t("Cerrar todo", "Close all")}
                </button>
              </div>
            </div>
          </div>

          <div className="xima-learn-index-search-row">
            <label className="xima-learn-index-search">
              <IoSearchOutline aria-hidden="true" />
              <input
                type="search"
                value={guideSearchTerm}
                onChange={(event) => setGuideSearchTerm(event.target.value)}
                placeholder={t("Buscar guia, regla, modo o funcion", "Search guide, rule, mode, or feature")}
                aria-label={t("Buscar dentro de las guias de Aprender", "Search inside Learn guides")}
              />
              {guideSearchTerm ? (
                <button
                  type="button"
                  onClick={() => setGuideSearchTerm("")}
                  aria-label={t("Limpiar busqueda", "Clear search")}
                >
                  <IoCloseCircleOutline aria-hidden="true" />
                </button>
              ) : null}
            </label>
            <span className="xima-learn-index-search-count">
              {isGuideSearchActive
                ? `${visibleGuideCount} ${t("resultados", "results")}`
                : `${LEARN_COMPLETE_GUIDE_COUNT} ${t("paginas disponibles", "available pages")}`}
            </span>
          </div>

          <div className="xima-learn-complete-group-list">
            {visibleGuideGroups.map((group, groupIndex) => {
              const isOpen = isGuideSearchActive || openGuideGroups.has(group.label);
              const panelId = `xima-learn-guide-group-${groupIndex}`;
              return (
                <section
                  key={group.label}
                  className={`xima-learn-complete-group ${isOpen ? "xima-learn-complete-group-open" : ""}`}
                >
                  <button
                    type="button"
                    className="xima-learn-complete-group-toggle"
                    onClick={() => toggleGuideGroup(group.label)}
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                    disabled={isGuideSearchActive}
                  >
                    <span className="xima-learn-complete-group-title">
                      <span>{group.label}</span>
                      <small>
                        {group.items.length} {t("paginas", "pages")}
                      </small>
                    </span>
                    <IoChevronDownOutline aria-hidden="true" />
                  </button>

                  {isOpen ? (
                    <div id={panelId} className="xima-learn-expanded-link-grid xima-learn-complete-link-grid">
                      {group.items.map((item) => (
                        <a
                          key={item.path}
                          href={hrefFor(item.path)}
                          onClick={(event) => {
                            event.preventDefault();
                            goTo(item.path);
                          }}
                        >
                          <span className="xima-learn-expanded-item-copy">
                            <span>{item.label}</span>
                          </span>
                          <IoChevronForwardOutline aria-hidden="true" />
                        </a>
                      ))}
                    </div>
                  ) : null}
                </section>
              );
            })}
            {visibleGuideGroups.length === 0 ? (
              <div className="xima-learn-index-empty">
                {t("No encontramos guias con esa busqueda.", "No guides matched that search.")}
              </div>
            ) : null}
          </div>
        </section>

        <section className="xima-learn-panel xima-learn-guides-panel">
          <div className="xima-learn-section-title">
            <span className="xima-learn-section-icon xima-learn-section-icon-blue">
              <IoBookOutline aria-hidden="true" />
            </span>
            <div>
              <small>{t("Guias practicas", "Practical Guides")}</small>
              <h2>{t("Biblioteca XIMA", "XIMA Library")}</h2>
            </div>
          </div>

          <div className="xima-learn-guide-grid">
            {books.map((book) => {
              const card = getBookCardCopy(book, language);
              return (
                <article key={book.slug} className={`xima-learn-guide-card xima-learn-guide-${card.theme}`}>
                  <div className="xima-learn-guide-image">
                    <img
                      src={card.image || book.coverImage}
                      alt={card.title}
                      loading="lazy"
                    />
                  </div>
                  <div className="xima-learn-guide-copy">
                    <h3>{card.title}</h3>
                    <p>{card.description}</p>
                  </div>
                  <a
                    href={
                      isPublicRoute
                        ? publicLearnRoute(`biblioteca/${book.slug}`)
                        : `/game/learn/${book.slug}`
                    }
                    onClick={(event) => {
                      event.preventDefault();
                      goTo(
                        isPublicRoute
                          ? publicLearnRoute(`biblioteca/${book.slug}`)
                          : `/game/learn/${book.slug}`
                      );
                    }}
                  >
                    <span>{t("Leer guia", "Read guide")}</span>
                    <IoBookOutline aria-hidden="true" />
                  </a>
                </article>
              );
            })}
          </div>
        </section>

        <section className="xima-learn-help-panel">
          <div className="xima-learn-help-copy">
            <span className="xima-learn-help-icon">
              <IoHelpCircleOutline aria-hidden="true" />
            </span>
            <div>
              <small>{t("Necesitas ayuda?", "Need help?")}</small>
              <h2>{t("Canal Oficial", "Official Channel")}</h2>
              <p>{t("Unete al canal oficial de XIMA en YouTube.", "Join the official XIMA YouTube channel.")}</p>
            </div>
          </div>
          <a
            href={OFFICIAL_YOUTUBE_LINKS[language] || OFFICIAL_YOUTUBE_LINKS.en}
            target="_blank"
            rel="noreferrer"
            className="xima-learn-channel-button"
          >
            <IoLogoYoutube aria-hidden="true" />
            <span>{t("Visitar canal de YouTube", "Visit YouTube channel")}</span>
            <IoOpenOutline aria-hidden="true" />
          </a>
        </section>
      </div>
    </div>
  );
};

export default Learn;
